import { FoodsSelectServerPagination, Typography } from "@foodpilot/foods";
import { useTheme } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import { useTranslation } from "react-i18next";
import { useGetAllIngredientLines } from "src/api/client-api/ingredientLines/useGetAllIngredientLine";
import { IngredientFormType } from "../../IngredientForm.types";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { ControllerRenderProps } from "react-hook-form";
import type { IngredientFormData } from "../../IngredientForm.schema";
import { Dispatch, SetStateAction } from "react";
import { FoodsField } from "src/components/EntityForm/FoodsFields";
import { FormFieldLoadError } from "src/components/EntityForm/FormFieldLoadError";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";

type FilliereFieldProps = {
    ingredient: IngredientFormType;
    onChange: Dispatch<SetStateAction<IngredientFormType>>;
    required?: boolean;
    field: ControllerRenderProps<IngredientFormData, "ingredientLine">;
    appearance?: "default" | "product-v2";
};
export const FilliereField = (props: FilliereFieldProps) => {
    const { appearance = "default", ingredient, onChange, required, field } = props;

    const { search, setSearch, delayedSearch } = useDelayedSearch();
    const {
        allIngredientLines = [],
        isError,
        refetch,
    } = useGetAllIngredientLines({
        name: delayedSearch,
    });
    const { t } = useTranslation();
    const theme = useTheme();
    const isSearchFound = (value: string) => {
        if (delayedSearch === "") {
            return true;
        }

        return value.toLowerCase().includes(delayedSearch.toLowerCase());
    };

    const requiredText = required ? "*" : "";
    const hasUsableOptions =
        allIngredientLines.length > 0 ||
        (ingredient.ingredientLine !== null && ingredient.ingredientLine !== undefined);
    const label = `${t("filiere")}${requiredText}`;
    const loadError =
        isError ?
            <FormFieldLoadError
                message={t("ingredientLines.loadError", "Ingredient lines could not be loaded.")}
                retryLabel={t("common.retry", "Retry")}
                onRetry={refetch}
            />
        :   null;
    const selector =
        !isError || hasUsableOptions ?
            <FoodsSelectServerPagination
                onChange={(data) => {
                    field.onChange(data);
                    onChange((prevState) => {
                        return {
                            ...prevState,
                            ingredientLine: data,
                        };
                    });
                }}
                options={allIngredientLines}
                selectedOption={ingredient.ingredientLine}
                getId={(item) => item["@id"]}
                getName={(item) => item.name}
                textOptions={{
                    placeholder: t("component.select.placeholder"),
                    searchPlaceholder: t("component.select.searchplaceholder"),
                    emptyOptionsText: t("component.select.nooptions"),
                    defaultValueText: t("component.select.defaultvalue"),
                    noResult: t("component.select.noresultfound"),
                }}
                search={search}
                setSearch={setSearch}
                isSearchFound={isSearchFound}
                BoxProps={{ width: "100%" }}
                BoxOptionsProps={{ width: theme.spacing(56.25) }}
            />
        :   null;

    if (appearance === "product-v2") {
        return (
            <FoodsField label={label}>
                {loadError}
                {selector && <FoodsSelectTriggerAdapter accessibleLabel={label}>{selector}</FoodsSelectTriggerAdapter>}
            </FoodsField>
        );
    }

    return (
        <FormControl size="small">
            <Typography variant="body-semibold" sx={{ paddingBottom: "0.5rem" }}>
                {label}
            </Typography>
            {loadError}
            {selector}
        </FormControl>
    );
};
